import { useEffect, useMemo, useState } from "react";
import API, { getErrorMessage } from "../api/axios";
import { hasFullAccess, isAdmin } from "../auth/auth";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { ACCENT, NAVY } from "../theme";
import PageContainer from "../components/ui/PageContainer";
import RefreshButton from "../components/ui/RefreshButton";
import LoadingState from "../components/ui/LoadingState";
import EmptyState from "../components/ui/EmptyState";
import StatusBadge from "../components/ui/StatusBadge";

// Same color-to-tone mapping used in Phases 3–6 and 8: maps this file's
// existing MUI Chip `color` names (STATUS_COLOR / PRIORITY_COLOR) to the
// shared STATUS_COLORS tone keys. This file doesn't use "secondary" for
// anything, so unlike some earlier pages there's no lossy approximation here.
const MUI_COLOR_TO_TONE = {
  success: "ok",
  info: "info",
  primary: "info",
  warning: "warn",
  error: "danger",
  default: "neutral",
};

const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH"];
const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "ON_HOLD", "CLOSED"];

const PRIORITY_COLOR = { LOW: "default", MEDIUM: "warning", HIGH: "error" };
const STATUS_COLOR = {
  OPEN: "info",
  IN_PROGRESS: "primary",
  ON_HOLD: "warning",
  CLOSED: "success",
};

const statusLabel = (k) =>
  ({
    OPEN: "Open",
    IN_PROGRESS: "In Progress",
    ON_HOLD: "On Hold",
    CLOSED: "Closed",
  })[k] || k;

const priorityLabel = (k) =>
  ({ LOW: "Low", MEDIUM: "Medium", HIGH: "High" })[k] || k;

// ── Phase 13 (Design Gap Audit item M) ──────────────────────────────────────
// Real relative-age helper, matching the prototype's "2 h ago" / "1 d ago"
// rowsub style — computed from the existing createdAt field, no new data.
function relativeAge(dateStr) {
  if (!dateStr) return null;
  const ms = Date.now() - new Date(dateStr + "Z").getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const emptyForm = { subject: "", description: "", priority: "MEDIUM" };

export default function Tickets() {
  const toast = useToast();
  const admin = isAdmin(); // ONLY admin can resolve/manage tickets
  const fullAccess = hasFullAccess(); // admin or manager (used for "see all tickets")
  const canRaise = !admin; // everyone EXCEPT admin can raise (incl. HR manager)

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("ALL");

  // Admin / manager "Manage ticket" dialog
  const [editTicket, setEditTicket] = useState(null);
  const [editStatus, setEditStatus] = useState("OPEN");
  const [editPriority, setEditPriority] = useState("MEDIUM");
  const [editResponse, setEditResponse] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    API.get("/tickets")
      .then((r) => setList(r.data))
      .catch(() => toast.error("Could not load tickets"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Auto-refresh when the user returns to this browser tab (silent)
  useEffect(() => {
    const onFocus = () => {
      if (!showForm && !editTicket) {
        API.get("/tickets")
          .then((r) => setList(r.data))
          .catch(() => {});
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, editTicket]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async () => {
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      await API.post("/tickets", form);
      toast.success("Ticket raised");
      setForm(emptyForm);
      setShowForm(false);
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not raise ticket"));
    } finally {
      setSaving(false);
    }
  };

  const openManage = (t) => {
    setEditTicket(t);
    setEditStatus(t.status || "OPEN");
    setEditPriority(t.priority || "MEDIUM");
    setEditResponse(t.adminResponse || "");
  };

  const saveManage = async () => {
    setUpdating(true);
    try {
      await API.put(`/tickets/${editTicket.id}`, {
        status: editStatus,
        priority: editPriority,
        adminResponse: editResponse,
      });
      toast.success("Ticket updated");
      setEditTicket(null);
      fetchAll();
    } catch {
      toast.error("Could not update ticket");
    } finally {
      setUpdating(false);
    }
  };

  const counts = useMemo(() => {
    const c = { OPEN: 0, IN_PROGRESS: 0, ON_HOLD: 0, CLOSED: 0 };
    list.forEach((t) => {
      if (c[t.status] !== undefined) c[t.status]++;
    });
    return c;
  }, [list]);

  const visible =
    filter === "ALL" ? list : list.filter((t) => t.status === filter);

  const statCards = [
    { key: "OPEN", label: "Open", color: "#2563eb", bg: "#eff6ff" },
    {
      key: "IN_PROGRESS",
      label: "In Progress",
      color: "#7c3aed",
      bg: "#f5f3ff",
    },
    { key: "ON_HOLD", label: "On Hold", color: "#ea580c", bg: "#fff7ed" },
    { key: "CLOSED", label: "Closed", color: "#16a34a", bg: "#f0fdf4" },
  ];

  const filterChips = [
    { key: "ALL", label: "All" },
    ...STATUS_OPTIONS.map((s) => ({ key: s, label: statusLabel(s) })),
  ];

  return (
    <PageContainer sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          pl: { xs: 5, md: 0 },
        }}
      >
        <Typography
          sx={{ color: "text.secondary", fontSize: 14, fontWeight: 600 }}
        >
          {admin
            ? "Track and resolve support requests"
            : "Raise a request and track its status"}
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <RefreshButton onClick={fetchAll} />

          {/* Everyone EXCEPT admin can raise tickets (incl. HR manager) */}
          {canRaise && (
            <Button
              variant={showForm ? "outlined" : "contained"}
              startIcon={showForm ? <CloseIcon /> : <AddIcon />}
              onClick={() => {
                setShowForm(!showForm);
                setForm(emptyForm);
              }}
            >
              {showForm ? "Close" : "Raise Ticket"}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((s) => (
          <Grid item xs={6} md={3} key={s.key}>
            <Paper
              sx={{
                p: 2.5,
                background: s.bg,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}
              >
                {s.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: s.color,
                  lineHeight: 1.1,
                }}
              >
                {counts[s.key]}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Raise ticket form — everyone EXCEPT admin */}
      {canRaise && showForm && (
        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>
            Raise a New Ticket
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder="Brief summary of your issue"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Priority"
                name="priority"
                value={form.priority}
                onChange={onChange}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {priorityLabel(p)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description}
                onChange={onChange}
                placeholder="Describe your issue or request in detail…"
                multiline
                minRows={4}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              onClick={saving ? undefined : onSubmit}
              disabled={saving}
            >
              {saving ? "Submitting…" : "Submit Ticket"}
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                setShowForm(false);
                setForm(emptyForm);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Status filter */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 3 }}
      >
        {filterChips.map((f) => {
          const active = filter === f.key;
          return (
            <Chip
              key={f.key}
              label={f.label}
              onClick={() => setFilter(f.key)}
              variant={active ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                bgcolor: active ? NAVY : "transparent",
                color: active ? "#fff" : "text.primary",
                "&:hover": { bgcolor: active ? NAVY : "action.hover" },
              }}
            />
          );
        })}
      </Stack>

      {/* List */}
      {loading ? (
        <Paper sx={{ p: 2 }}>
          <LoadingState variant="rows" rows={4} columns={4} />
        </Paper>
      ) : visible.length === 0 ? (
        <Paper sx={{ py: 2 }}>
          <EmptyState
            icon={
              <ConfirmationNumberOutlinedIcon
                sx={{ fontSize: 28, color: ACCENT }}
              />
            }
            title="No tickets found"
            description={
              filter !== "ALL"
                ? "Try a different status filter."
                : admin
                  ? "No tickets have been raised yet."
                  : "Raise your first ticket to get started."
            }
          />
        </Paper>
      ) : (
        <Stack spacing={2}>
          {visible.map((t) => (
            <Paper key={t.id} sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 0.5 }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                      {t.subject}
                    </Typography>
                    <Chip size="small" label={`#${t.id}`} variant="outlined" />
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                      whiteSpace: "pre-wrap",
                      mb: 1,
                    }}
                  >
                    {t.description}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <StatusBadge
                      label={statusLabel(t.status)}
                      tone={
                        MUI_COLOR_TO_TONE[STATUS_COLOR[t.status]] || "neutral"
                      }
                    />
                    <StatusBadge
                      label={`Priority: ${priorityLabel(t.priority)}`}
                      tone={
                        MUI_COLOR_TO_TONE[PRIORITY_COLOR[t.priority]] ||
                        "neutral"
                      }
                    />
                    {fullAccess && (
                      <Typography
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      >
                        Raised by {t.createdBy}
                      </Typography>
                    )}
                    {t.createdAt && (
                      <Tooltip
                        title={new Date(t.createdAt + "Z").toLocaleString()}
                      >
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          · {relativeAge(t.createdAt)}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
                {admin && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openManage(t)}
                  >
                    Manage
                  </Button>
                )}
              </Box>

              {t.adminResponse && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: "background.default",
                    borderRadius: 1.5,
                    borderLeft: "3px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Response{t.resolvedBy ? ` · ${t.resolvedBy}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                    {t.adminResponse}
                  </Typography>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      {/* Admin / manager manage dialog */}
      <Dialog
        open={!!editTicket}
        onClose={() => setEditTicket(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Manage Ticket #{editTicket?.id}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            {editTicket?.subject}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "text.secondary",
              whiteSpace: "pre-wrap",
              mb: 2,
            }}
          >
            {editTicket?.description}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Priority"
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {priorityLabel(p)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Response to user"
                value={editResponse}
                onChange={(e) => setEditResponse(e.target.value)}
                placeholder="Write your response / resolution…"
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setEditTicket(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={updating ? undefined : saveManage}
            disabled={updating}
          >
            {updating ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
